import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {   RiMapPinLine,   RiInformationLine, RiLoader4Line, RiExternalLinkLine } from 'react-icons/ri';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../config';

const DistrictPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchDistrictData = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/districts/get_by_slug.php?slug=${id}`);
                const result = await response.json();
                if (response.ok) {
                    setData(result);
                } else {
                    navigate('/travel');
                }
            } catch (error) {
                console.error('Error fetching district data:', error);
                navigate('/travel');
            } finally {
                setLoading(false);
            }
        };
        fetchDistrictData();
    }, [id, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <RiLoader4Line className="animate-spin text-emerald-500 text-4xl" />
            </div>
        );
    }

    if (!data) return null;

    const categoryNames = Object.keys(data.categories || {});

    return (
        <div className="bg-white min-h-screen pt-32">
            <div className="container mx-auto px-6 max-w-7xl pb-24">
                <div className="grid lg:grid-cols-12 gap-16 items-start">
                    {/* Left: Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-12"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <RiMapPinLine className="text-emerald-500" size={24} />
                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.3em]">Regional Deep Dive</span>
                        </div>

                        <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold text-slate-900 font-display mb-8">
                            {data.name}<span className="text-emerald-600">.</span>
                        </h1>

                        <p className="text-lg sm:text-xl text-slate-500 font-medium font-sans leading-relaxed mb-12 max-w-4xl italic">
                            "{data.description}"
                        </p>

                        <div className="grid lg:grid-cols-2 gap-16">
                            <div className="space-y-12">
                                {categoryNames.length > 0 ? (
                                    categoryNames.map((catName) => (
                                        <div key={catName}>
                                            <h3 className="flex items-center gap-3 text-[10px] font-black text-slate-900 uppercase tracking-[0.2em] mb-6 font-display">
                                                <div className="w-8 h-[1px] bg-slate-200" />
                                                {catName}
                                            </h3>
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                {data.categories[catName].map((place) => (
                                                    <motion.div
                                                        key={place.id}
                                                        whileHover={{ y: -4 }}
                                                        onClick={() => navigate(`/travel/place/${place.id}`)}
                                                        className="flex items-center gap-4 p-5 bg-slate-50 rounded-2xl border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/30 transition-all cursor-pointer group"
                                                    >
                                                        <div className="w-12 h-12 flex-shrink-0 rounded-xl overflow-hidden bg-slate-200">
                                                            <img src={place.image_url} alt={place.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-bold text-slate-900 truncate font-display">{place.name}</h4>
                                                            <div className="flex items-center gap-1 text-[9px] text-slate-400 font-black uppercase tracking-widest mt-1 font-sans">
                                                                Explore More <RiExternalLinkLine className="text-emerald-500" />
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-10 border-2 border-dashed border-slate-100 rounded-[32px] text-center">
                                        <RiInformationLine className="mx-auto text-slate-300 mb-4" size={32} />
                                        <p className="text-slate-400 font-medium">We are currently curating the best travel destinations for {data.name}. Stay tuned!</p>
                                    </div>
                                )}
                            </div>

                            {/* Visual */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8 }}
                                className="sticky top-40"
                            >
                                <div className="relative aspect-[16/10] lg:aspect-[5/4] rounded-[20px] overflow-hidden shadow-3xl">
                                    <img src={data.image_url} alt={data.name} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                                    <div className="absolute bottom-12 left-12 right-12">
                                        <span className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.3em] mb-2 block">Climate Zone</span>
                                        <h4 className="text-2xl font-bold text-white uppercase tracking-tight">Tropical Archive</h4>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default DistrictPage;
